import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { PortalDataService } from '../services/portal-data.service';

/**
 * The wizard is a full read/write form -- fine for a brand-new publication,
 * but once a publication has left Draft (submitted, under review, queried,
 * etc.) its content and documents are the record and must not be
 * re-editable here, even via a bookmarked/typed `?id=` URL. The backend
 * already rejects these writes outside Draft (see PublicationPolicy::update
 * / ::uploadDocument), but leaving the wizard reachable produces a
 * confusing dead-end instead of a clear "you can't edit this" message.
 *
 * `paid` is let through too: it means payment cleared but the applicant
 * never reached the wizard's own final submit (e.g. the tab closed first),
 * so the record isn't submitted either -- the wizard already resumes one of
 * these straight at the Payment step's "Submit Publication" button (see
 * `partial.isPaid` in PublicationWizardComponent's hydrate effect).
 */
export const draftEditGuard: CanActivateFn = (route) => {
  const id = route.queryParamMap.get('id');
  if (!id) return true; // Creating a brand-new publication.

  const data = inject(PortalDataService);
  const router = inject(Router);

  return data.getPublication(id).pipe(
    map((publication) => {
      if (publication.status === 'draft' || publication.status === 'paid') return true;
      return router.createUrlTree(['/app/publications', id]);
    }),
    catchError(() => of(router.createUrlTree(['/app/publications'])))
  );
};
